import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { AREAS, AGES } from "./config";
import { responsesToCsv } from "./csv";
import { loadResponses } from "./service";
import "./communityVoice.css";

const counts = (rows, key) => rows.reduce((result, row) => {
  const values = Array.isArray(row[key]) ? row[key] : [row[key] || "No answer"];
  values.forEach((value) => { result[value] = (result[value] || 0) + 1; });
  return result;
}, {});

function Breakdown({ title, data }) {
  return (
    <section className="cv-card">
      <h2>{title}</h2>
      {Object.entries(data).sort((a, b) => b[1] - a[1]).map(([label, count]) => (
        <div className="cv-bar" key={label}>
          <span>{label}</span><strong>{count}</strong><i style={{ width: `${Math.min(100, count * 10)}%` }} />
        </div>
      ))}
    </section>
  );
}

export default function AdminDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ area: "", age: "", claimed: "" });

  useEffect(() => { loadResponses().then(setRows).finally(() => setLoading(false)); }, []);
  const shown = useMemo(() => rows.filter((row) => (
    (!filters.area || row.residentArea === filters.area)
    && (!filters.age || row.ageGroup === filters.age)
    && (!filters.claimed || String(row.tokenClaimed) === filters.claimed)
  )), [rows, filters]);
  const update = (event) => setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));
  const exportCsv = () => {
    const blob = new Blob([responsesToCsv(shown)], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "uun-community-voice-barkantine-festival.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };
  const unaware = shown.filter((row) => row.heardOfUUN === "No").length;

  return (
    <div className="cv-admin">
      <div className="cv-admin-title">
        <div><p className="cv-kicker">Admin · Barkantine Festival 2026</p><h1>Community Voice results</h1></div>
        <div className="cv-inline-actions">
          <Link className="cv-button" to="/admin/community-voice/ai-digital-skills">AI &amp; Digital Skills results</Link>
          <Link className="cv-button cv-secondary" to="/admin/community-voice/check-token">Check a food token</Link>
        </div>
      </div>
      <div className="cv-filters">
        <label>Resident area<select name="area" value={filters.area} onChange={update}><option value="">All areas</option>{AREAS.map((area) => <option key={area}>{area}</option>)}</select></label>
        <label>Age group<select name="age" value={filters.age} onChange={update}><option value="">All ages</option>{AGES.map((age) => <option key={age}>{age}</option>)}</select></label>
        <label>Token<select name="claimed" value={filters.claimed} onChange={update}><option value="">All tokens</option><option value="true">Claimed</option><option value="false">Unclaimed</option></select></label>
        <button type="button" className="cv-button" onClick={exportCsv}><Download size={18} /> Export filtered CSV</button>
      </div>
      {loading ? <p>Loading results…</p> : (
        <>
          <div className="cv-stats">
            {[
              ["Completed surveys", shown.length],
              ["Barkantine Estate", shown.filter((row) => row.residentArea === "Barkantine Estate").length],
              ["Tokens claimed", shown.filter((row) => row.tokenClaimed).length],
              ["Tokens unclaimed", shown.filter((row) => !row.tokenClaimed).length],
              ["Had not heard of UUN", shown.length ? `${Math.round((unaware / shown.length) * 100)}%` : "0%"],
              ["Interested / maybe volunteering", shown.filter((row) => ["Yes", "Maybe"].includes(row.volunteerInterest)).length],
            ].map(([label, value]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}
          </div>
          <div className="cv-dashboard-grid">
            <Breakdown title="Responses by area" data={counts(shown, "residentArea")} />
            <Breakdown title="Responses by age" data={counts(shown, "ageGroup")} />
            <Breakdown title="Community connection" data={counts(shown, "connectionRating")} />
            <Breakdown title="Top local issues" data={counts(shown, "localIssues")} />
            <Breakdown title="Most requested activities" data={counts(shown, "wantedActivities")} />
            <Breakdown title="Most requested services" data={counts(shown, "wantedServices")} />
            <Breakdown title="Awareness of UUN" data={counts(shown, "heardOfUUN")} />
            <Breakdown title="Volunteering interest" data={counts(shown, "volunteerInterest")} />
          </div>
          <section className="cv-card"><h2>One thing residents would change</h2>{shown.filter((row) => row.oneChange).length ? shown.filter((row) => row.oneChange).map((row) => <blockquote key={row.id}>{row.oneChange}<small>{row.residentArea} · {row.ageGroup}</small></blockquote>) : <p>No written answers in this selection.</p>}</section>
        </>
      )}
    </div>
  );
}
